import { ReactNode } from 'react'
import styled, { css } from 'styled-components'
import { GLOW } from './avatar'
import { TxState } from './chainTx'
import { Icon } from './Identity'
import { HeldNft } from './onchainReads'

/* ───────────────────────── numbered steps (approve → confirm) ───────────────────────── */
const StepList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 10px;
`
const StepItem = styled.li<{ $state: 'todo' | 'now' | 'done' }>`
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  padding: 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  ${({ $state }) =>
    $state === 'now' &&
    css`
      background: rgba(127, 212, 232, 0.07);
      border-color: rgba(127, 212, 232, 0.3);
    `}
  .n {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 15px;
    font-weight: 800;
    color: ${({ $state }) => ($state === 'done' ? '#0b1220' : $state === 'now' ? '#8fe3f5' : 'rgba(255,255,255,0.55)')};
    background: ${({ $state }) => ($state === 'done' ? '#8ff0c4' : $state === 'now' ? 'rgba(127, 212, 232, 0.16)' : 'rgba(255, 255, 255, 0.06)')};
  }
  .t {
    display: grid;
    gap: 8px;
    min-width: 0;
  }
  strong {
    font-size: 16px;
    line-height: 32px;
    color: ${({ theme }) => theme.colors.text};
  }
`
export function Steps({ children }: { children: ReactNode }) {
  return <StepList>{children}</StepList>
}
export function Step({ n, title, state, children, testId }: { n: number; title: string; state: 'todo' | 'now' | 'done'; children?: ReactNode; testId?: string }) {
  return (
    <StepItem $state={state} data-testid={testId} data-state={state}>
      <span className="n" aria-hidden>
        {state === 'done' ? <Icon name="check" size={18} /> : n}
      </span>
      <div className="t">
        <strong>
          {title}
          {state === 'done' && <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}> (done)</span>}
        </strong>
        {children}
      </div>
    </StepItem>
  )
}

/* ───────────────────────── transaction status line ───────────────────────── */
const Status = styled.p<{ $tone: 'info' | 'ok' | 'bad' }>`
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: ${({ $tone }) => ($tone === 'ok' ? '#8ff0c4' : $tone === 'bad' ? '#ff9b9b' : '#8fe3f5')};
  a {
    color: inherit;
    text-decoration: underline;
  }
`
const Spin = styled.span`
  width: 14px;
  height: 14px;
  margin-top: 3px;
  flex: none;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-right-color: transparent;
  animation: spin 800ms linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
/** wallet → "Confirm in your wallet", pending → "Waiting for BNB Chain" + BscScan, success → done text, error → the reason. */
export function TxStatus({ state, error, hash, done, testId = 'tx-status' }: { state: TxState; error: string | null; hash: string | null; done: string; testId?: string }) {
  if (state === 'idle') return null
  const scan = hash ? (
    <a href={`https://bscscan.com/tx/${hash}`} target="_blank" rel="noreferrer">
      View on BscScan
    </a>
  ) : null
  if (state === 'error')
    return (
      <Status $tone="bad" role="alert" data-testid={testId} data-state="error">
        <Icon name="info" size={16} />
        <span>{error}</span>
      </Status>
    )
  if (state === 'success')
    return (
      <Status $tone="ok" role="status" data-testid={testId} data-state="success">
        <Icon name="check" size={16} />
        <span>
          {done} {scan}
        </span>
      </Status>
    )
  return (
    <Status $tone="info" role="status" aria-live="polite" data-testid={testId} data-state={state}>
      <Spin aria-hidden />
      <span>
        {state === 'wallet' ? 'Confirm in your wallet. Nothing is sent until you approve it there.' : 'Sent. Waiting for BNB Chain…'} {scan}
      </span>
    </Status>
  )
}

/* ───────────────────────── NFT picker (one tile per collection held) ───────────────────────── */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`
export const PickTile = styled.button<{ $lvl?: number | null }>`
  position: relative;
  display: grid;
  gap: 4px;
  justify-items: center;
  padding: 6px 6px 8px;
  border-radius: 16px;
  min-height: 44px;
  cursor: pointer;
  color: inherit;
  font: inherit;
  background: rgba(255, 255, 255, 0.04);
  border: 2px solid transparent;
  img {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 12px;
    background: radial-gradient(100% 100% at 50% 0%, rgba(154, 123, 255, 0.3), #0d1530);
  }
  .nm {
    font-size: 13px;
    font-weight: 700;
    text-transform: capitalize;
  }
  .sub {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textSubtle};
    font-variant-numeric: tabular-nums;
  }
  .lv {
    position: absolute;
    top: 10px;
    left: 10px;
    min-height: 20px;
    padding: 0 7px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 800;
    display: inline-flex;
    align-items: center;
    color: #0b1220;
    background: ${({ $lvl }) => ($lvl === 2 ? '#c6a8ff' : '#8fe3f5')};
  }
  &[aria-pressed='true'] {
    border-color: ${GLOW};
    box-shadow: 0 0 18px -4px ${GLOW};
    background: rgba(127, 212, 232, 0.08);
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid ${GLOW};
    outline-offset: 2px;
  }
`
export const pickKey = (n: { collection: string }) => n.collection.toLowerCase()

export function NftPicker({ nfts, selected, onSelect, disabled }: { nfts: HeldNft[]; selected: string | null; onSelect: (n: HeldNft) => void; disabled?: boolean }) {
  return (
    <Grid role="group" aria-label="Your CADINU NFTs" data-testid="nft-picker">
      {nfts.map((n) => (
        <PickTile
          key={n.collection}
          type="button"
          $lvl={n.level}
          aria-pressed={selected === pickKey(n)}
          disabled={disabled}
          onClick={() => onSelect(n)}
          data-testid={`pick-${n.character}-${n.level}`}
          aria-label={`${n.character} level ${n.level}, ${n.tokenIds.length} held`}
        >
          {n.image ? <img src={n.image} alt="" loading="lazy" /> : <span style={{ width: '100%', aspectRatio: '1' }} />}
          <span className="lv">L{n.level}</span>
          <span className="nm">{n.character}</span>
          <span className="sub">{n.tokenIds.length > 1 ? `${n.tokenIds.length} held` : `#${n.tokenIds[0]}`}</span>
        </PickTile>
      ))}
    </Grid>
  )
}

/* ───────────────────────── small form bits ───────────────────────── */
export const Check = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  font-size: 15px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  input {
    width: 20px;
    height: 20px;
    margin: 0;
    accent-color: #7fd4e8;
    flex: none;
  }
`
export const TextInput = styled.input`
  width: 100%;
  min-width: 0;
  min-height: 46px;
  padding: 0 12px;
  border-radius: 12px;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text};
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.16);
  &:focus-visible {
    outline: 2px solid ${GLOW};
    border-color: transparent;
  }
  &:disabled {
    opacity: 0.7;
  }
`
export const Warn = styled.p`
  margin: 0;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #ffd27a;
  background: rgba(255, 178, 55, 0.1);
  border: 1px solid rgba(255, 178, 55, 0.28);
`
export const Danger = styled(Warn)`
  color: #ffb4b4;
  background: rgba(255, 90, 90, 0.08);
  border-color: rgba(255, 90, 90, 0.3);
`
export const Select = styled.select`
  min-height: 44px;
  min-width: 96px;
  padding: 0 12px;
  border-radius: 12px;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text};
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.16);
  &:focus-visible {
    outline: 2px solid ${GLOW};
    border-color: transparent;
  }
  option {
    color: #0b1220;
  }
`
/** A two-or-more option switch (level 1 / level 2). */
export const Segmented = styled.div`
  display: inline-flex;
  padding: 4px;
  gap: 4px;
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.1);
  button {
    min-height: 40px;
    padding: 0 16px;
    border-radius: 10px;
    border: 0;
    font: inherit;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.textSubtle};
    background: transparent;
  }
  button[aria-pressed='true'] {
    color: #0b1220;
    background: linear-gradient(135deg, #8fe3f5, #9a7bff);
  }
  button:focus-visible {
    outline: 2px solid ${GLOW};
  }
`
const SumRow = styled.div<{ $strong?: boolean; $tone?: 'ok' | 'bad' }>`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: ${({ $strong }) => ($strong ? '17px' : '14px')};
  font-weight: ${({ $strong }) => ($strong ? 800 : 500)};
  font-variant-numeric: tabular-nums;
  color: ${({ $tone, theme }) => ($tone === 'ok' ? '#8ff0c4' : $tone === 'bad' ? '#ff9b9b' : theme.colors.text)};
  span:first-child {
    flex: 1;
    min-width: 0;
    color: ${({ $strong, theme }) => ($strong ? theme.colors.text : theme.colors.textSubtle)};
  }
  span:last-child {
    flex: none;
    white-space: nowrap;
    text-align: right;
  }
`
export function Sum({ label, value, strong, tone, testId }: { label: ReactNode; value: ReactNode; strong?: boolean; tone?: 'ok' | 'bad'; testId?: string }) {
  return (
    <SumRow $strong={strong} $tone={tone} data-testid={testId}>
      <span>{label}</span>
      <span>{value}</span>
    </SumRow>
  )
}
